import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, CircularProgress, Button, Alert } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SchematicBuilder from "./SchematicBuilder";
import api from "../api";

const PRIMARY = "#005E60";

function SchematicEditWrapper() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [schematic, setSchematic] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadSchematic = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/schematic/load/${id}`);
      setSchematic(res.data.schematic);
      setProjectData(res.data.project);
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (
        err?.response?.status === 404 ||
        detail.toLowerCase().includes("not found")
      ) {
        setError(
          "Schematic not found. This project may not have a schematic yet.",
        );
      } else if (!err?.response) {
        setError("Cannot reach the server. Please check your connection.");
      } else {
        setError("Failed to load schematic. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadSchematic();
  }, [loadSchematic]);

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress sx={{ color: PRIMARY }} />
      </Box>
    );

  if (error)
    return (
      <Box
        sx={{
          p: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          maxWidth: 480,
          mx: "auto",
        }}
      >
        <Alert severity="error" sx={{ borderRadius: "8px", width: "100%" }}>
          {error}
        </Alert>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadSchematic}
            sx={{
              color: PRIMARY,
              borderColor: PRIMARY,
              textTransform: "none",
              "&:hover": { backgroundColor: "#e6f4f4" },
            }}
          >
            Try Again
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/project/${id}`)}
            sx={{
              color: PRIMARY,
              borderColor: PRIMARY,
              textTransform: "none",
              "&:hover": { backgroundColor: "#e6f4f4" },
            }}
          >
            ← Back to Project
          </Button>
        </Box>
      </Box>
    );

  return (
    <SchematicBuilder
      projectId={id}
      initialSchematic={schematic}
      initialProject={projectData}
    />
  );
}

export default SchematicEditWrapper;
